import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CameraDisplay } from "../CameraDisplay";
import { ImageProfile } from "../ImageProfile";
import { SensorConfig } from "../SensorConfig";

export interface LiveCaptureProps {
  selectedCamera: string | null;
  selectedStream: string | null;
  isSwitchingStream?: boolean;
  onStreamingChange?: (isStreaming: boolean) => void;
  onMediaStreamChange?: (stream: MediaStream | null) => void;
  isRecording?: boolean;
  isIntervalCapturing?: boolean;
  onIntervalCapturingChange?: (value: boolean) => void;
  isRebooting?: boolean;
  rebootSecondsLeft?: number;
}

export const LiveCapture = ({
  selectedCamera,
  selectedStream,
  isSwitchingStream,
  onStreamingChange,
  onMediaStreamChange,
  isRecording,
  isIntervalCapturing = false,
  onIntervalCapturingChange,
  isRebooting = false,
  rebootSecondsLeft = 0,
}: LiveCaptureProps) => {
  const { t } = useTranslation("capture");
  useEffect(() => {
    if (!selectedStream || isSwitchingStream) {
      onStreamingChange?.(false);
    }
  }, [selectedStream, isSwitchingStream, onStreamingChange]);

  const showConnecting =
    selectedCamera && (!selectedStream || isSwitchingStream);

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6">
      <p className="text-xl font-bold">{t("live.title")}</p>

      {showConnecting && (
        <div className="relative">
          <div className="aspect-video w-full rounded-md bg-black/5" />
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {t("stream.loading")}
          </div>
        </div>
      )}

      {selectedCamera && selectedStream && !isSwitchingStream && (
        <>
          <CameraDisplay
            selectedMxid={selectedCamera}
            selectedSensorName={selectedStream}
            onStreamingChange={onStreamingChange}
            onMediaStreamChange={onMediaStreamChange}
            isRecording={isRecording}
            isRebooting={isRebooting}
            rebootSecondsLeft={rebootSecondsLeft}
          />
          <ImageProfile
            selectedCamera={selectedCamera}
            selectedStream={selectedStream}
          />
          <SensorConfig
            selectedCamera={selectedCamera}
            selectedStream={selectedStream}
            isIntervalCapturing={isIntervalCapturing}
            onIntervalCapturingChange={onIntervalCapturingChange}
          />
        </>
      )}
    </div>
  );
};
